import * as React from "react";

import {IsShowPricing}  from "@Common/logic/check-show-pricing";
import {cst, cstAssign} from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {
    FIELD_VALUES,
}                       from "@Common/types/apis/payloads/next/reporting.transaction.option-values";
import {
    addYear,
    min as minDate,
    parseDate,
    today,
}                       from "@Common/utils/date-object";
import {chain}          from "@Common/utils/fs";
import {upperCase1}     from "@Common/utils/strings";

import {COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

type Agent     = Types.Apis.Payloads.Next.Agent.GetAll.Response[number];
type Branch    = Types.Apis.Payloads.Next.Branch.GetAll.Response[number];
type FormModel = Types.Xpresso.Account.Admin.Reporting.Transaction.FormModel;

const CRITERIA_OPTIONS = FIELD_VALUES.map((value) => {
    let label:string;
    switch (value) {
        case "agentName":          label = "User Name"; break;
        case "agentLinkId":        label = "User ID"; break;
        case "creditCardTotal":    label = "CC Total"; break;
        case "mlsNumber":          label = "MLS Number"; break;
        case "justListedJustSold": label = "Just Listed / Just Sold"; break;
        case "subcategoryName":    label = "Sub-Category Name"; break;

        default: label = (
            value
                .split(/(?=[A-Z])/)
                .map(upperCase1)
                .map(word => word.toUpperCase() === "ID" ? word.toUpperCase() : word)
                .join(" ")
        ); break;
    }
    return {label, value};
});

const CRITERIA_PRICING_VALUES:FormModel['options'][number][] = [
    "billingTotal",
    "creditCardTotal",
    "promoCodeTotal",
    "salesTax",
    "shippingTotal",
    "subtotal",
    "total",
];

const PROGRAM_DEFAULT_OPTIONS = [
    {name: "All",         idProgram: "all"},
    {name: "Main System", idProgram: "main"},
];

export const TransactionFormState = ({next: rootNext}:{
    next: (v:Types.Xpresso.Account.Admin.Reporting.Transaction.FormStateNext) => JSX.Element,
}) => cst<{
    agentsLoad   : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Agent.GetAll.Response>,
    apis         : Types.Apis.Loaders.XpressoLoadApis,
    branchesLoad : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Branch.GetAll.Response>,
    company      : Types.Apis.Loaders.LoadCompanyNext,
    programsLoad : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Program.GetList.Response>,
    regionsLoad  : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Region.GetList.Response>,
    showPricing  : boolean,
    userLevels   : Types.Apis.Loaders.LoadUserLevelsNext,
    valueState   : Types.Common.React.UseState.UseStateNext<FormModel>,
}>(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("userLevels"),
    cstAssign("regionsLoad", ({apis, userLevels: {userLevel}}, next) => (
        Load2({next, fetch: userLevel === COMPANY_LEVEL ? apis.next.region.getRegionList : () => Promise.resolve([])})
    )),
    cstAssign("branchesLoad", ({apis, userLevels: {userLevel}}, next) => (
        Load2({next, fetch: userLevel === COMPANY_LEVEL ? apis.next.getBranchList : () => Promise.resolve([])})
    )),
    cstAssign("agentsLoad", ({apis}, next) => Load2({next, fetch: apis.next.getAgentList})),
    cstAssign("programsLoad", ({apis}, next) => Load2({next, fetch: apis.next.program.getList})),
    cstAssign("showPricing", ({}, next) => IsShowPricing({next})),
    cstAssign("valueState", ({}, next) => (
        UseState({next, initValue: {
            deliveryType : "all",
            orderStatus  : "completed",
            programId    : "all",
        } as FormModel}))
    ),
    ({agentsLoad,
        branchesLoad,
        programsLoad,
        regionsLoad: {value: regions},
        showPricing,
        userLevels: {userLevel},
        valueState: {onChange: setValue, value}
    }) => {
        const branches = chain(
            branchesLoad.value ?? [],
            (branches:Branch[]) => value.regionId ? branches.filter(makeBranchByRegionIdFilter(value.regionId)) : branches,
        ) as Branch[];

        return rootNext({
            branches,
            value,

            agents   : chain(
                agentsLoad.value ?? [],
                (agents:Agent[]) => {
                    if (value.branchId) {
                        return agents.filter(makeAgentByBranchIdFilter(value.branchId))
                    } else if (value.regionId) {
                        return agents.filter(makeAgentByBranchesFilter(branches));
                    } else {
                        return agents;
                    }
                },
            ),
            programs : PROGRAM_DEFAULT_OPTIONS.concat(programsLoad.value?.map(({name, idProgram}) => ({idProgram, name: name || idProgram})) ?? []),
            regions  : regions ?? [],

            criteria : showPricing ? CRITERIA_OPTIONS : CRITERIA_OPTIONS.filter(o => !CRITERIA_PRICING_VALUES.includes(o.value)),

            maxDate : chain(
                today(),
                (todayDate) => value.startDate ? minDate(todayDate, addYear(value.startDate, 1)) : todayDate
            ),
            minDate : value.endDate || value.startDate ? addYear(value.endDate ?? value.startDate, -1) : parseDate(new Date("1970")),

            setFieldValue : (field, fieldValue) => {
                if(field === "regionId") {
                    const keepAgent  = !fieldValue || agentIdInRegionId(value.agentId, fieldValue as string, agentsLoad.value, branchesLoad.value);
                    const keepBranch = !fieldValue || branchIdInRegionId(value.branchId, fieldValue as string, branchesLoad.value);
                    setValue({
                        ...value,
                        ...(!keepAgent && {agentId: null}),
                        ...(!keepBranch && {branchId: null}),
                        [field]: fieldValue,
                    });
                } else if (field === "branchId") {
                    const keepAgent = !fieldValue || agentIdInBranchId(value.agentId, fieldValue as string, agentsLoad.value);
                    setValue({
                        ...value,
                        ...(!keepAgent && {agentId: null}),
                        [field]: fieldValue,
                    });
                } else {
                    setValue({...value, [field]: fieldValue});
                }
            },

            showOfficeInput : userLevel === COMPANY_LEVEL,
            showRegionInput : userLevel === COMPANY_LEVEL && regions?.length > 0 && true,

            validationError : (() => {
                if (!value.startDate || !value.endDate) {
                    return "Please select a date range";
                } else if (!value.emailAddresses?.length) {
                    return "Please enter at least one email address";
                } else if (value.emailAddresses?.some(isNotAnEmailAddress)) {
                    return `Please fix the following email address: "${value.emailAddresses.find(isNotAnEmailAddress)}"`;
                } else if (!value.options?.length) {
                    return "Please select at least one criteria";
                } else {
                    return null;
                }
            })()
        });
    },
);

const agentIdInBranchId = (agentId:string, branchId:string, agents:Agent[]) => (
    agents
        ?.filter(makeAgentByBranchIdFilter(branchId))
        ?.map(a => a.idAgent)
        ?.includes(agentId)
);

const agentIdInRegionId = (agentId:string, regionId:string, agents:Agent[], branches:Branch[]) => (
    agents
        ?.filter(makeAgentByRegionIdFilter(regionId, branches))
        ?.map(a => a.idAgent)
        ?.includes(agentId)
);

const branchIdInRegionId = (branchId:string, regionId:string, branches:Branch[]) => (
    branches
        ?.filter(makeBranchByRegionIdFilter(regionId))
        ?.map(b => b.idBranch)
        ?.includes(branchId)
);

const isAnEmailAddress = (email:string) => (
    /^[A-z0-9-.]+@[A-z0-9-.]+\.[A-z0-9]+$/.test(email)
);
const isNotAnEmailAddress = (email:string) => !isAnEmailAddress(email);

const makeAgentByBranchesFilter = (branches:Branch[]) => {
    const branchIds = branches?.map(b => b.idBranch);
    return (agent:Agent) => branchIds.includes(agent.idBranch)
};

const makeAgentByBranchIdFilter = (branchId:string) => (
    (agent:Agent) => agent.idBranch === branchId
);

const makeAgentByRegionIdFilter = (regionId:string, branches:Branch[]) => (
    makeAgentByBranchesFilter(branches?.filter(makeBranchByRegionIdFilter(regionId)))
);

const makeBranchByRegionIdFilter = (regionId:string) => (
    (branch:Branch) => branch.idRegion === regionId
);

